import React, { useEffect, useMemo, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import ProductCard from "./ProductCard";
import { kategoriAdi, kategoriAdlariYukle, urunleriYukle } from "../data/katalog";
import { filtreGruplari, filtreyeUyar, sirala } from "../data/urunNitelikleri";
import { akilliAra } from "../data/akilliArama";

// "Ispatula" / "ispatula" / "ıspatula" hepsi eşleşsin diye Türkçe harfleri sadeleştirir.
const aramaMetni = (s) =>
  (s || "")
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .trim();

// Bazı kategori listelerinde slug bir sayfa değil, tanıtım görseli adıdır (ör. "Alcipan.jpg").
const gorselSlugMu = (s) => /\.(jpe?g|png|gif|webp)$/i.test(s || "");

/** Alt kategori listesi + marka filtresi. Mobilde katlanır, masaüstünde sabit. */
const FiltrePaneli = ({
  t,
  kategoriler,
  aktifSlug,
  yerel,
  markalar,
  secilenMarkalar,
  markaDegistir,
  temizle,
  nitelikGruplari = [],
  seciliNitelik = {},
  nitelikDegistir,
}) => (
  <>
    {kategoriler.length > 0 && (
      <div className="mb-8">
        <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-eyebrow text-muted">
          {t("shop.subCategories")}
        </h2>
        <ul className="space-y-0.5 border-l border-line">
          {kategoriler.map((k) => {
            const aktif = k.slug === aktifSlug;
            return (
              <li key={k.slug}>
                <Link
                  to={`/urunler/${k.slug}`}
                  state={{ baslik: yerel(k) }}
                  aria-current={aktif ? "page" : undefined}
                  className={`-ml-px block border-l-2 py-1.5 pl-3 text-[13.5px] transition-colors ${
                    aktif
                      ? "border-ink font-medium text-ink"
                      : "border-transparent text-body hover:border-ink/30 hover:text-ink"
                  }`}
                >
                  {yerel(k)}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    )}

    {markalar.length > 1 && (
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-[11px] font-semibold uppercase tracking-eyebrow text-muted">
            {t("shop.brands")}
          </h2>
          {(secilenMarkalar.length > 0 || Object.values(seciliNitelik).some((l) => l.length)) && (
            <button
              onClick={temizle}
              className="text-[11px] text-muted transition-colors hover:text-accent"
            >
              {t("shop.clearFilters")}
            </button>
          )}
        </div>

        <ul className="space-y-1">
          {markalar.map(([m, adet]) => (
            <li key={m}>
              <label className="flex cursor-pointer items-center gap-2.5 py-1 text-[13.5px] text-body transition-colors hover:text-ink">
                <input
                  type="checkbox"
                  checked={secilenMarkalar.includes(m)}
                  onChange={() => markaDegistir(m)}
                  className="h-3.5 w-3.5 accent-accent"
                />
                <span className="flex-1">{m}</span>
                <span className="text-[12px] tabular-nums text-muted">
                  {adet}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>
    )}

    {/* Ürün adlarından çıkarılan nitelikler: voltaj, akü durumu, renk, ölçü/ambalaj */}
    {nitelikGruplari.map((g) => (
      <div key={g.anahtar} className="mt-8">
        <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-eyebrow text-muted">
          {t(`shop.facets.${g.anahtar}`)}
        </h2>
        <ul className={g.anahtar === "secenek" || g.anahtar === "voltaj" ? "flex flex-wrap gap-1.5" : "space-y-1"}>
          {g.degerler.map(([deger, adet]) => {
            const secili = (seciliNitelik[g.anahtar] || []).includes(deger);
            const etiket = g.anahtar === "aku" ? t(`shop.facets.aku_${deger}`) : deger;
            return g.anahtar === "secenek" || g.anahtar === "voltaj" ? (
              <li key={deger}>
                <button
                  type="button"
                  onClick={() => nitelikDegistir(g.anahtar, deger)}
                  aria-pressed={secili}
                  className={`rounded-md border px-2.5 py-1 text-[12.5px] tabular-nums transition-colors ${
                    secili ? "border-ink bg-ink text-white" : "border-line text-body hover:border-ink/40 hover:text-ink"
                  }`}
                >
                  {etiket} <span className={secili ? "text-white/60" : "text-muted"}>({adet})</span>
                </button>
              </li>
            ) : (
              <li key={deger}>
                <label className="flex cursor-pointer items-center gap-2.5 py-1 text-[13.5px] text-body transition-colors hover:text-ink">
                  <input
                    type="checkbox"
                    checked={secili}
                    onChange={() => nitelikDegistir(g.anahtar, deger)}
                    className="h-3.5 w-3.5 accent-accent"
                  />
                  <span className="flex-1">{etiket}</span>
                  <span className="text-[12px] tabular-nums text-muted">{adet}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>
    ))}
  </>
);

/** Sıralama seçimi — ürün sayısının yanında durur */
const SiralamaSecimi = ({ t, deger, degistir }) => (
  <label className="flex items-center gap-2 text-[12.5px] text-muted">
    <span className="hidden sm:inline">{t("shop.sort.label")}</span>
    <select
      value={deger}
      onChange={(e) => degistir(e.target.value)}
      className="rounded-md border border-line bg-white px-2.5 py-1.5 text-[13px] text-ink outline-none focus:border-ink/40"
    >
      {["onerilen", "az", "za", "marka"].map((s) => (
        <option key={s} value={s}>
          {t(`shop.sort.${s}`)}
        </option>
      ))}
    </select>
  </label>
);

/**
 * Ürünler bölümü.
 *
 * /urunler            -> ana kategoriler
 * /urunler/:slug      -> alt kategoriler ve (varsa) o kategorinin ürünleri
 *
 * Kategori ağacı public/data/*.json dosyalarından okunur.
 * Gerçek ürünler (marka, boyut, özellik) public/data/boya-urunleri.json içindedir;
 * ileride başka kategoriler için de aynı yapı kullanılabilir.
 */
export default function Urunler() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const konum = useLocation();

  // Kategori başlığı: tıklanarak gelindiyse yönlendirmeyle taşınır,
  // adres doğrudan açıldıysa ana kategori listesinden aranır.
  const [baslik, setBaslik] = useState(konum.state?.baslik || null);

  const lang = (i18n.language || "tr").split("-")[0];
  const yerel = (item) =>
    (lang !== "tr" && item[`title_${lang}`]) || item.title;

  const [kategoriler, setKategoriler] = useState([]);
  const [kardesler, setKardesler] = useState([]); // yan paneldeki liste
  const [urunler, setUrunler] = useState([]);
  const [dizin, setDizin] = useState(null); // slug -> ust slug
  const [kategoriAdlari, setKategoriAdlari] = useState(null); // slug -> [tr, en, fr, ar]
  const [ataZinciri, setAtaZinciri] = useState([]); // [{slug, baslik}, ...] köke yakından uzağa
  const [yukleniyor, setYukleniyor] = useState(true);

  const [secilenMarkalar, setSecilenMarkalar] = useState([]);
  const [seciliNitelik, setSeciliNitelik] = useState({}); // { voltaj: ["18V"], renk: ["Beyaz"], ... }
  const [siralama, setSiralama] = useState("onerilen");
  const [arama, setArama] = useState("");
  const [modalUrun, setModalUrun] = useState(null);

  const kokMu = !slug;
  const [sorgu] = useSearchParams();
  const markaSecimi = kokMu ? sorgu.get("marka") : null;
  const aramaParametresi = kokMu ? sorgu.get("q") : null; // navbar'daki arama kutusundan gelir

  // Sayfa, marka ya da navbar araması değişince arama kutusu buna göre sıfırlanır/doldurulur.
  useEffect(() => {
    setArama(aramaParametresi || "");
  }, [slug, markaSecimi, aramaParametresi]);

  // --- kategori ağacı ---
  useEffect(() => {
    let iptal = false;
    setYukleniyor(true);
    setSecilenMarkalar([]);
    setSeciliNitelik({});

    const dosya = kokMu ? "urunler" : slug;

    fetch(`/data/${dosya}.json`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => !iptal && setKategoriler(Array.isArray(d) ? d : []))
      .catch(() => !iptal && setKategoriler([]))
      .finally(() => !iptal && setYukleniyor(false));

    return () => {
      iptal = true;
    };
  }, [slug, kokMu]);

  // --- kategori dizini (bir kez): hangi kategori hangisinin altında ---
  useEffect(() => {
    fetch("/data/_kategori-dizini.json")
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => setDizin(d || {}))
      .catch(() => setDizin({}));
  }, []);

  // --- yan panel listesi: bulunduğun kategorinin KARDEŞLERİ ---
  // Böylece bir alt kategoriye girince liste kaybolmaz, aradan başkasına geçebilirsin.
  //
  // ÖNEMLİ: eğer bu sayfanın kendisi bir ANA kategoriyse (ust === ""), kardeşleri
  // kök listesi (20 ana kategori) olurdu — o listeyi panelde GÖSTERMİYORUZ.
  // Ana kategoriler arasında geçiş için ızgara zaten yeterli; panel sadece bir
  // ana kategorinin İÇİNDEKİ alt kategoriler arasında geçiş için var.
  useEffect(() => {
    if (kokMu || !dizin) {
      setKardesler([]);
      return;
    }

    const ust = dizin[slug] ?? null; // "" -> bu sayfa bir ana kategori, null -> bilinmiyor

    if (ust === "" || ust === null) {
      setKardesler([]);
      return;
    }

    let iptal = false;
    fetch(`/data/${ust}.json`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => !iptal && setKardesler(Array.isArray(d) ? d : []))
      .catch(() => !iptal && setKardesler([]));

    return () => {
      iptal = true;
    };
  }, [slug, kokMu, dizin]);

  // --- breadcrumb: tam ata zinciri (Ürünler / Ana Kategori / Alt Kategori / ...) ---
  // dizin sadece slug -> üst slug tutar, başlık tutmaz (dosyayı küçük tutmak için).
  // Bu yüzden zinciri slug olarak çıkarıp, her seviyenin başlığını kendi
  // EBEVEYNİNİN listesinden buluyoruz — kök seviye için urunler.json'dan.
  useEffect(() => {
    if (kokMu || !dizin) {
      setAtaZinciri([]);
      return;
    }

    let iptal = false;

    (async () => {
      // slug'dan köke doğru çık, sonra ters çevir (köke yakından uzağa)
      const slugZinciri = [];
      const gorulen = new Set();
      let s = slug;
      while (s && !gorulen.has(s)) {
        gorulen.add(s);
        slugZinciri.unshift(s);
        const ust = dizin[s];
        if (ust === "" || ust == null) break;
        s = ust;
      }

      const parcalar = [];
      for (let i = 0; i < slugZinciri.length; i++) {
        const mevcut = slugZinciri[i];
        const ebeveyn = i === 0 ? "urunler" : slugZinciri[i - 1];
        try {
          const r = await fetch(`/data/${ebeveyn}.json`);
          const liste = r.ok ? await r.json() : [];
          const bulunan = (Array.isArray(liste) ? liste : []).find(
            (k) => k.slug === mevcut
          );
          parcalar.push({
            slug: mevcut,
            baslik: bulunan ? yerel(bulunan) : mevcut.replace(/-/g, " "),
          });
        } catch {
          parcalar.push({ slug: mevcut, baslik: mevcut.replace(/-/g, " ") });
        }
      }

      if (!iptal) {
        setAtaZinciri(parcalar);
        if (parcalar.length) setBaslik(parcalar[parcalar.length - 1].baslik);
      }
    })();

    return () => {
      iptal = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, kokMu, dizin, lang]);

  // Kategori sayfası: sekme başlığı ve arama motoru açıklaması kategori adıyla
  useEffect(() => {
    if (kokMu || !baslik) return;
    document.title = `${baslik} — ${t("siteName")}`;
    const meta = document.querySelector('meta[name="description"]');
    const onceki = meta?.getAttribute("content");
    meta?.setAttribute("content", t("shop.categoryMeta", {
        kategori: baslik,
        defaultValue: "{{kategori}} ürünleri — Akkuzey Yapı Malzemeleri, Zeytinburnu / İstanbul",
      }));
    return () => {
      if (meta && onceki != null) meta.setAttribute("content", onceki);
    };
  }, [kokMu, baslik, t]);

  // Yönlendirmeyle gelen başlığı hemen kullan (zincir yüklenene kadar geçici)
  useEffect(() => {
    if (konum.state?.baslik) setBaslik(konum.state.baslik);
  }, [konum.state]);

  // --- ürün kataloğu (bir kez) ---
  // Dosya listesi src/data/katalog.js içinde; ürün detay sayfası ve ana sayfa da aynı listeyi kullanır.
  useEffect(() => {
    let iptal = false;
    urunleriYukle().then((liste) => {
      if (!iptal) setUrunler(liste);
    });
    kategoriAdlariYukle().then((a) => {
      if (!iptal) setKategoriAdlari(a);
    });
    return () => {
      iptal = true;
    };
  }, []);

  // Bu sayfaya DOĞRUDAN atanmış ürünler var mı? (ör. /urunler/insaat-boyalari)
  // Varsa bu gerçek bir ürün/yaprak sayfasıdır — kendi alt kategorileri olsa
  // bile (orijinal veriden miras, artık kullanılmıyor) burada ürünler asıldır.
  const dogrudanUrunler = useMemo(() => {
    if (kokMu || !urunler.length) return [];
    return urunler.filter((u) => u.kategori === slug);
  }, [urunler, slug, kokMu]);

  const dogrudanUrunVarMi = dogrudanUrunler.length > 0;

  // Bu sayfanın altındaki TÜM kategoriler (torunlar dahil) — dizinden çıkarılır.
  const altKume = useMemo(() => {
    const kume = new Set();
    if (!kokMu && dizin) {
      const cocuklar = {};
      for (const [s, ust] of Object.entries(dizin)) (cocuklar[ust] ||= []).push(s);
      const yigin = [slug];
      while (yigin.length) {
        for (const c of cocuklar[yigin.pop()] || []) {
          if (!kume.has(c)) {
            kume.add(c);
            yigin.push(c);
          }
        }
      }
    }
    kategoriler.forEach((k) => kume.add(k.slug));
    return kume;
  }, [dizin, slug, kokMu, kategoriler]);

  // Bu sayfada gösterilecek ürünler:
  // - /urunler/insaat-boyalari  -> kategorisi bu olanlar (doğrudan)
  // - /urunler/boya-firca-...   -> altındaki tüm kategorilerin ürünleri (toplama)
  const sayfaUrunleri = useMemo(() => {
    if (dogrudanUrunVarMi) return dogrudanUrunler;
    if (kokMu || !urunler.length) return [];
    return urunler.filter((u) => altKume.has(u.kategori));
  }, [urunler, altKume, kokMu, dogrudanUrunVarMi, dogrudanUrunler]);

  // Kategori kutucuklarındaki ürün sayısı: her ürün kendi kategorisinden köke kadar tüm atalarına sayılır.
  const urunSayilari = useMemo(() => {
    const say = {};
    if (!dizin) return say;
    for (const u of urunler) {
      const gorulen = new Set();
      let s = u.kategori;
      while (s && !gorulen.has(s)) {
        gorulen.add(s);
        say[s] = (say[s] || 0) + 1;
        s = dizin[s];
      }
    }
    return say;
  }, [urunler, dizin]);

  // İçeriği olan kategoriler: kendisinde ya da altındaki herhangi bir seviyede ürün veya vitrin görseli bulunan.
  // Tıklanınca boş sayfa açan kutucuklar ızgarada ve yan panelde gösterilmez.
  const icerikliKategoriler = useMemo(() => {
    if (!dizin || !urunler.length) return null; // yüklenene kadar filtreleme yok (kutucuklar titremesin)
    const kume = new Set();
    const isaretle = (s) => {
      const gorulen = new Set();
      while (s && !gorulen.has(s)) {
        gorulen.add(s);
        kume.add(s);
        s = dizin[s];
      }
    };
    for (const [s, ust] of Object.entries(dizin)) {
      if (gorselSlugMu(s)) isaretle(ust); // vitrin görseli: üst kategorisi dolu sayılır
    }
    for (const s of Object.keys(urunSayilari)) isaretle(s);
    return kume;
  }, [dizin, urunler, urunSayilari]);

  const gorunurMu = (k) => gorselSlugMu(k.slug) || !icerikliKategoriler || icerikliKategoriler.has(k.slug);
  const gorunurKategoriler = useMemo(
    () => kategoriler.filter(gorunurMu),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kategoriler, icerikliKategoriler]
  );

  // Markalar ve sayıları
  const markalar = useMemo(() => {
    const say = new Map();
    for (const u of sayfaUrunleri) if (u.marka) say.set(u.marka, (say.get(u.marka) || 0) + 1);
    return [...say.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [sayfaUrunleri]);

  // Nitelik filtreleri (voltaj, akü, renk, ölçü) — marka seçimine göre daralır
  const nitelikGruplari = useMemo(
    () => filtreGruplari(secilenMarkalar.length ? sayfaUrunleri.filter((u) => secilenMarkalar.includes(u.marka)) : sayfaUrunleri),
    [sayfaUrunleri, secilenMarkalar]
  );
  const nitelikDegistir = (anahtar, deger) =>
    setSeciliNitelik((o) => {
      const liste = o[anahtar] || [];
      return { ...o, [anahtar]: liste.includes(deger) ? liste.filter((x) => x !== deger) : [...liste, deger] };
    });
  const filtreleriTemizle = () => {
    setSecilenMarkalar([]);
    setSeciliNitelik({});
  };
  const aktifFiltreSayisi = secilenMarkalar.length + Object.values(seciliNitelik).reduce((t, l) => t + l.length, 0);

  // Filtre uygula
  const gosterilecek = useMemo(() => {
    const q = aramaMetni(arama);
    return sirala(
      sayfaUrunleri.filter((u) => {
        if (secilenMarkalar.length && !secilenMarkalar.includes(u.marka)) return false;
        if (q && !aramaMetni(u.ad).includes(q)) return false;
        return filtreyeUyar(u, seciliNitelik);
      }),
      siralama
    );
  }, [sayfaUrunleri, secilenMarkalar, arama, seciliNitelik, siralama]);

  // Ana sayfada (Ürün Kategorileri) arama: tüm kategorilerdeki tüm ürünler.
  // Her kelime ad veya markada geçmeli ("sgs mala" -> SGS'nin malaları).
  // ?marka=SGS ile gelindiyse (footer'daki marka kartları) sadece o marka.
  const genelAramaAktif =
    kokMu && (aramaMetni(arama).length > 0 || Boolean(markaSecimi));
  // Yazım hatasına dayanıklı arama ("silkon" → silikon); düzeltme yapıldıysa kullanıcıya gösterilir
  const genelArama = useMemo(() => {
    if (!genelAramaAktif) return { liste: [], duzeltme: null };
    const havuz = markaSecimi ? urunler.filter((u) => u.marka === markaSecimi) : urunler;
    if (!aramaMetni(arama)) return { liste: sirala(havuz, siralama), duzeltme: null };
    const { sonuclar, duzeltme } = akilliAra(havuz, arama);
    return { liste: siralama === "onerilen" ? sonuclar : sirala(sonuclar, siralama), duzeltme };
  }, [genelAramaAktif, arama, urunler, markaSecimi, siralama]);
  const genelSonuclar = genelArama.liste;

  const markaDegistir = (m) =>
    setSecilenMarkalar((ö) =>
      ö.includes(m) ? ö.filter((x) => x !== m) : [...ö, m]
    );

  const urunVar = sayfaUrunleri.length > 0;

  // Panelde gösterilecek liste:
  //   - Bu sayfaya DOĞRUDAN ürün atanmışsa (gerçek bir ürün sayfasıysa),
  //     panelde KARDEŞLERİ göster — ör. "İnşaat Boyaları" sayfasındaysak,
  //     üstündeki kategorinin diğer çocukları (Endüstriyel Boyalar, Tiner, ...).
  //     Bu sayfanın kendi alt kategorileri olsa bile (orijinal veriden miras,
  //     artık kullanılmıyor) burada ürünler asıldır, onlar değil.
  //   - Değilse (henüz bir ürün grubuna inilmemişse), bu sayfanın KENDİ
  //     çocuklarını göster — ör. "Boya Fırça ve Rulo Grubu" sayfasındaysak:
  //     İnşaat Boyaları, Endüstriyel Boyalar, ...
  // Hiçbir durumda ana kategoriler (kök liste) panelde gösterilmez.
  // Alt kategorisi olan (yaprak olmayan) sayfalarda kutucuk ızgarası hep üstte durur;
  // altındaki ürünler varsa ızgaranın altında listelenir.
  const izgaraGoster =
    !genelAramaAktif && !dogrudanUrunVarMi && kategoriler.length > 0 && !yukleniyor;

  // Izgara zaten alt kategorileri gösteriyorsa panelde tekrar listelenmez (sadece marka filtresi kalır).
  const panelKategoriler = izgaraGoster
    ? []
    : (dogrudanUrunVarMi ? kardesler : kategoriler.length > 0 ? kategoriler : kardesler).filter(
        (k) => !gorselSlugMu(k.slug) && gorunurMu(k)
      );
  const panelAktifSlug = dogrudanUrunVarMi
    ? slug
    : kategoriler.length > 0
    ? null
    : slug;

  // Yan panel: kategori ya da marka seçeneği varsa görünür.
  const yanPanelVar =
    !kokMu && (panelKategoriler.length > 1 || (urunVar && (markalar.length > 1 || nitelikGruplari.length > 0)));

  // Kategori kutucuğu: slug bir görsel dosyasıysa (tanıtım kutucuğu) büyüt, değilse sayfasına git.
  const kategoriAc = (item) => {
    if (gorselSlugMu(item.slug)) setModalUrun(item);
    else navigate(`/urunler/${item.slug}`, { state: { baslik: yerel(item) } });
  };

  const gorselKlasor = kokMu ? "urunler" : slug;

  // Geri butonu: bir üst kategoriye döner. "" veya bilinmiyorsa köke (Ürünler) döner.
  const ustSlug = dizin ? dizin[slug] ?? null : null;
  const geriDon = () =>
    navigate(ustSlug ? `/urunler/${ustSlug}` : "/urunler");

  return (
    <section className="container-page section">
      {!kokMu && (
        <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-2">
          <button
            onClick={geriDon}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-body transition-colors hover:text-ink"
          >
            <span aria-hidden="true" className="arrow-back" />
            {t("ui.back")}
          </button>

          {/* Breadcrumb — tam ata zinciri: Ürünler / Ana Kategori / Alt Kategori / ... */}
          <nav
            aria-label="Breadcrumb"
            className="scrollbar-hide flex items-center gap-2 overflow-x-auto whitespace-nowrap text-[13px] text-muted"
          >
            <Link to="/urunler" className="transition-colors hover:text-ink">
              {t("products")}
            </Link>

            {(ataZinciri.length
              ? ataZinciri
              : [{ slug, baslik: baslik || slug.replace(/-/g, " ") }]
            ).map((parca, i, dizi) => (
              <React.Fragment key={parca.slug}>
                <span aria-hidden="true" className="text-line">/</span>
                {i === dizi.length - 1 ? (
                  <span className="font-medium text-ink">{parca.baslik}</span>
                ) : (
                  <Link
                    to={`/urunler/${parca.slug}`}
                    state={{ baslik: parca.baslik }}
                    className="transition-colors hover:text-ink"
                  >
                    {parca.baslik}
                  </Link>
                )}
              </React.Fragment>
            ))}
          </nav>
        </div>
      )}

      <div className="page-head flex flex-wrap items-end justify-between gap-4">
        <div>
          {markaSecimi && (
            <Link
              to="/urunler"
              className="mb-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-body transition-colors hover:text-ink"
            >
              <span aria-hidden="true" className="arrow-back" />
              {t("ui.productCategories")}
            </Link>
          )}
          <h1 className="page-title">
            {markaSecimi
              ? t("shop.brandProducts", { marka: markaSecimi })
              : kokMu
              ? t("ui.productCategories")
              : baslik || t("ui.subProducts")}
          </h1>
        </div>

        {(urunVar || kokMu) && (
          <input
            type="search"
            value={arama}
            onChange={(e) => setArama(e.target.value)}
            placeholder={t(
              kokMu && !markaSecimi ? "shop.searchAllPlaceholder" : "shop.searchPlaceholder"
            )}
            aria-label={t(
              kokMu && !markaSecimi ? "shop.searchAllPlaceholder" : "shop.searchPlaceholder"
            )}
            className="w-full max-w-xs rounded-md border border-line px-3.5 py-2.5 text-[14px] text-ink outline-none transition-colors placeholder:text-muted focus:border-ink/30 sm:w-64"
          />
        )}
      </div>

      {/* Ana kategoriler: tam kadraj fotoğraf, üzerinde koyu geçiş ve başlık */}
      {izgaraGoster && kokMu && (
        <ul className="grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 lg:grid-cols-4">
          {gorunurKategoriler.map((item) => {
            const adet = urunSayilari[item.slug] || 0;
            return (
              <li key={item.slug}>
                <button
                  onClick={() => kategoriAc(item)}
                  className="group relative block aspect-[4/3] w-full overflow-hidden rounded-xl bg-canvas text-start shadow-sm ring-1 ring-black/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <img
                    src={`/images/productImages/${gorselKlasor}/${item.img}`}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.07]"
                  />
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent transition-opacity duration-300 group-hover:from-black/90"
                  />
                  {adet > 0 && (
                    <span className="absolute end-2.5 top-2.5 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold tabular-nums text-ink shadow-sm backdrop-blur">
                      {t("shop.productCount", { count: adet })}
                    </span>
                  )}
                  <span className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-3 sm:p-4">
                    <span className="line-clamp-2 text-[14px] font-semibold leading-snug text-white drop-shadow sm:text-[16px]">
                      {yerel(item)}
                    </span>
                    <span
                      aria-hidden="true"
                      className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-ink opacity-0 transition-all duration-300 group-hover:translate-x-0.5 group-hover:opacity-100 sm:flex"
                    >
                      <span className="arrow-forward" />
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Alt kategoriler: beyaz zeminli ürün görselleri, kadraj bozulmadan ortalanır */}
      {izgaraGoster && !kokMu && (
        <ul className="grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {gorunurKategoriler.map((item) => {
            const vitrin = gorselSlugMu(item.slug);
            const adet = vitrin ? 0 : urunSayilari[item.slug] || 0;
            return (
              <li key={item.slug}>
                <button
                  onClick={() => kategoriAc(item)}
                  className="group flex h-full w-full flex-col overflow-hidden rounded-xl border border-line bg-white text-start transition-all duration-300 hover:-translate-y-1 hover:border-transparent hover:shadow-[0_14px_34px_-12px_rgba(0,0,0,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <div className="relative aspect-[6/5] w-full overflow-hidden bg-white">
                    <img
                      src={`/images/productImages/${gorselKlasor}/${item.img}`}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-contain p-3 transition-transform duration-500 ease-out group-hover:scale-[1.06]"
                    />
                    {vitrin && (
                      <span className="absolute bottom-2 end-2 flex h-7 w-7 items-center justify-center rounded-full bg-ink/80 text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5" aria-hidden="true">
                          <circle cx="8.5" cy="8.5" r="5.5" />
                          <path d="M12.5 12.5 17 17M8.5 6v5M6 8.5h5" strokeLinecap="round" />
                        </svg>
                      </span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col justify-between gap-2 border-t border-line/70 bg-gradient-to-b from-white to-canvas/60 px-3.5 py-3 sm:px-4">
                    <h3 className="line-clamp-2 text-[13.5px] font-semibold leading-snug text-ink sm:text-[14px]">
                      {yerel(item)}
                    </h3>
                    <span className="flex items-center justify-between text-[12px] text-muted">
                      <span className="tabular-nums">
                        {vitrin
                          ? t("shop.tileZoom")
                          : adet > 0
                          ? t("shop.productCount", { count: adet })
                          : t("shop.tileExplore")}
                      </span>
                      <span
                        aria-hidden="true"
                        className="arrow-forward text-ink/40 transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-accent"
                      />
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {izgaraGoster && urunVar && (
        <h2 className="mb-6 mt-14 border-t border-line pt-10 text-[20px] font-semibold tracking-tight text-ink">
          {t("shop.categoryProducts")}
        </h2>
      )}

      <div className={yanPanelVar ? "flex flex-col gap-8 lg:flex-row" : ""}>
        {/* Yan panel — ana kategoriler hariç her sayfada durur */}
        {yanPanelVar && (
          <aside className="w-full shrink-0 lg:w-56">
            {/* Mobilde katlanır, masaüstünde hep açık */}
            <details className="group rounded-lg border border-line lg:hidden">
              <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-[14px] font-medium text-ink">
                {[
                  panelKategoriler.length > 0 && t("shop.subCategories"),
                  markalar.length > 1 && t("shop.brands"),
                  nitelikGruplari.length > 0 && t("shop.filters"),
                ]
                  .filter(Boolean)
                  .join(" · ")}
                <span className="text-muted transition-transform group-open:rotate-45">
                  +
                </span>
              </summary>
              <div className="border-t border-line p-4">
                <FiltrePaneli
                  t={t}
                  kategoriler={panelKategoriler}
                  aktifSlug={panelAktifSlug}
                  yerel={yerel}
                  markalar={markalar}
                  secilenMarkalar={secilenMarkalar}
                  markaDegistir={markaDegistir}
                  temizle={filtreleriTemizle}
                  nitelikGruplari={nitelikGruplari}
                  seciliNitelik={seciliNitelik}
                  nitelikDegistir={nitelikDegistir}
                />
              </div>
            </details>

            <div className="hidden lg:block">
              <FiltrePaneli
                t={t}
                kategoriler={panelKategoriler}
                aktifSlug={panelAktifSlug}
                yerel={yerel}
                markalar={markalar}
                secilenMarkalar={secilenMarkalar}
                markaDegistir={markaDegistir}
                temizle={filtreleriTemizle}
                nitelikGruplari={nitelikGruplari}
                seciliNitelik={seciliNitelik}
                nitelikDegistir={nitelikDegistir}
              />
            </div>
          </aside>
        )}


        {/* Ana alan */}
        <div className="min-w-0 flex-1">
          {genelAramaAktif ? (
            <>
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <p className="text-[13px] text-muted">
                  {arama.trim() && (
                    <>{t("shop.searchResultsFor", { q: arama.trim() })} · </>
                  )}
                  {t("shop.productCount", { count: genelSonuclar.length })}
                </p>
                <SiralamaSecimi t={t} deger={siralama} degistir={setSiralama} />
              </div>

              {genelArama.duzeltme && (
                <p className="mb-5 rounded-lg bg-canvas px-4 py-3 text-[13.5px] text-body">
                  {t("search.noResultFor", { q: arama.trim() })}{" "}
                  <button type="button" onClick={() => setArama(genelArama.duzeltme)} className="font-semibold text-accent hover:underline">
                    “{genelArama.duzeltme}”
                  </button>{" "}
                  {t("search.showingInstead")}
                </p>
              )}

              {genelSonuclar.length === 0 ? (
                <div className="rounded-lg border border-line bg-canvas p-10 text-center text-[15px] text-muted">
                  {t("shop.searchNoResult")}
                </div>
              ) : (
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {genelSonuclar.map((u) => (
                    <ProductCard key={u.id} urun={u} altBilgi={kategoriAdi(kategoriAdlari, u.kategori, lang)} />
                  ))}
                </div>
              )}
            </>
          ) : urunVar ? (
            <>
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <p className="text-[13px] text-muted">
                  {t("shop.productCount", { count: gosterilecek.length })}
                  {aktifFiltreSayisi > 0 && (
                    <>
                      {" · "}
                      <button type="button" onClick={filtreleriTemizle} className="font-medium text-accent hover:underline">
                        {t("shop.clearFiltersCount", { count: aktifFiltreSayisi })}
                      </button>
                    </>
                  )}
                </p>
                <SiralamaSecimi t={t} deger={siralama} degistir={setSiralama} />
              </div>

              {gosterilecek.length === 0 ? (
                <div className="rounded-lg border border-line bg-canvas p-10 text-center text-[15px] text-muted">
                  {t("shop.noResult")}
                </div>
              ) : (
                <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                  {gosterilecek.map((u) => (
                    <ProductCard key={u.id} urun={u} altBilgi={kategoriAdi(kategoriAdlari, u.kategori, lang)} />
                  ))}
                </div>
              )}
            </>
          ) : yukleniyor ? (
            <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="h-56 animate-pulse rounded-lg border border-line bg-canvas"
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {/* Görsel büyütme */}
      {modalUrun && (
        <div className="overlay" onClick={() => setModalUrun(null)}>
          <div
            className="w-full max-w-2xl animate-fadeIn overflow-hidden rounded-lg bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 border-b border-line px-6 py-4">
              <h3 className="text-[16px] font-semibold text-ink">
                {yerel(modalUrun)}
              </h3>
              <button
                onClick={() => setModalUrun(null)}
                aria-label={t("ui.close")}
                className="text-xl leading-none text-muted transition-colors hover:text-ink"
              >
                ×
              </button>
            </div>

            <div className="media max-h-[65vh] p-6">
              <img
                src={`/images/productImages/${gorselKlasor}/${modalUrun.img}`}
                alt={yerel(modalUrun)}
                className="max-h-[55vh] w-full object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
